// packages/shared-components/src/components/Keyboard/keyboardLayouts.ts
import type { AllKeyboardType } from "@eweadn-qiankun/shared-types";
import {
  EXTRA_KEYS_MATRIX,
  EXTRA_KEYS_MATRIX_BEIYING,
  EXTRA_KEYS_MATRIX_HUAFENDA,
  EXTRA_KEYS_MATRIX_ZHWX,
  EXTRA_KEYS_MATRIX_RONGYUAN,
  LAYOUT_DK87_KEYCODES,
  LAYOUT_DK75_SINGLE_KEYCODES,
  LAYOUT_DK63_MACHINE_KEYCODES,
  resolveMacroKeyCodeMatrix,
} from "@eweadn-qiankun/shared-utils";
/**
 * 键盘基础 KeyCode 矩阵配置
 * 作为默认布局，用于无 SDK 连接时的展示
 */
/** 65% 键盘 KeyCode 矩阵（5行） */
export const LAYOUT_63_KEYCODES: number[][] = [
  // Row 0: Esc + 数字行 + Home (15 keys)
  [0],
  // Row 1: Esc + 数字行 + Home (15 keys)
  [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42],
  // Row 2: Tab 行 + End (15 keys)
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49],
  // Row 3: Caps + Enter 前占位列
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40],
  // Row 4: LShift + 占位列 + Z 行；col11–13 与固件/SDK 一致：/?、↑、R-Shift
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 82, 229],
  // Row 5: 空格 col6；公版 DK63 右簇 RAlt、←、↓、→、Fn（DK63 V2 另见 wasdArrowSwap 专用矩阵）
  [224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 80, 81, 79, 61697],
];

/** ES68 等 68 配列：6 行 × 21 列（与 KEYBOARD_LAYOUTS["68"] / SDK 矩阵一致） */
export const LAYOUT_68_KEYCODES: number[][] = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [
    41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0, 0,
    0, 0,
  ],
];

/** 华奋达 BAT68：基于 DEEP68/`68`，最右侧自上而下为 Home / End / PgUp / PgDn */
export const LAYOUT_HUAFENDA68_KEYCODES: number[][] = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [
    41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 0, 80, 81, 79, 0, 0, 0, 0, 0,
    0,
  ],
];

/** Tank68 HE：华奋达 68 磁轴，右侧导航列同 BAT68，底行含 R-Ctrl */
export const LAYOUT_TANK68HE_KEYCODES: number[][] = [
  ...LAYOUT_HUAFENDA68_KEYCODES.slice(0, 5),
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0, 0,
    0, 0,
  ],
];

/** 65% 键盘 KeyCode 矩阵（5行） */
export const LAYOUT_68EVO_KEYCODES: number[][] = [
  // Row 0: Esc + 数字行 + Home (15 keys)
  [0],
  [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],

  // Row 1: Tab + QWERTY + End (15 keys)
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],

  // Row 2: Caps + ASDF + Enter + PgUp (14 keys)
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75],

  // Row 3: Shift + ZXCV + Shift + ↑ + PgDn (14 keys)
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],

  // Row 4: Ctrl + Win + Alt + Space + Alt + Fn + Ctrl + ←↓→ (10 keys)
  [224, 227, 226, 44, 44, 44, 230, 61697, 228, 80, 81, 79],
];

/** 80% TKL 键盘 KeyCode 矩阵（6行） */
export const LAYOUT_87_KEYCODES: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0, 0, 0,
    0,
  ],

  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 0, 0, 0,
    0,
  ],

  // Row 2: Tab + QWERTY + 导航键 (21 cols)
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 0, 0, 0,
    0,
  ],

  // Row 3: Caps + ASDF + Enter (21 cols)
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],

  // Row 4: Shift + ZXCV + Shift + 方向上 (21 cols)
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0],

  // ✅ Row 5: 控制行 + 方向键 (21 cols)
  // ✅ 修正：Col 11 改为 61697（80% 键盘的 Fn 键）
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 0, 0,
    0, 0,
  ],
  // Ctrl Win  Alt  空空空 Space 空空空 Alt  Fn    Menu Ctrl ←   ↓   →  空空空
  // 0    1    2    3  4  5  6    7  8  9  10   11    12   13   14  15  16  17 18 19 20
];

/** ES87 TKL：Esc 直连 F1，F 行含独立 F13(104) */
export const LAYOUT_ES87_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 104, 70, 71, 72, 0, 0,
    0, 0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 0, 0, 0,
    0,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 0, 0, 0,
    0,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 0, 0,
    0, 0,
  ],
];

export const LAYOUT_HUAFENDA87_KEYCODES: number[][] = [
  // Row 0: Esc + 空 + F1-F12 + 系统键 (21 cols)
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0, 0, 0, 0,
    0,
  ],

  // Row 1: 数字行 + Backspace + 导航键 (21 cols)
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 0, 0, 0,
    0,
  ],

  // Row 2: Tab + QWERTY + 导航键 (21 cols)
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 0, 0, 0,
    0,
  ],

  // Row 3: Caps + ASDF + Enter (21 cols)
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0, 0, 0, 0, 0],

  // Row 4: Shift + ZXCV + Shift + 方向上 (21 cols)
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0, 0],

  // ✅ Row 5: 控制行 + 方向键 (21 cols)
  // ✅ 修正：Col 11 改为 61697（80% 键盘的 Fn 键）
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 0, 0,
    0, 0,
  ],
  // Ctrl Win  Alt  空空空 Space 空空空 Alt  Fn    Menu Ctrl ←   ↓   →  空空空
  // 0    1    2    3  4  5  6    7  8  9  10   11    12   13   14  15  16  17 18 19 20
];

/**
 * 容园 X87S UI 矩阵：物理列宽同 huafenda87，键值由 SDK defaultLayout / 协议表维护。
 * 扩展键文案走 shared-utils/rongyuanKeyCodes，勿在此重复维护第二份矩阵。
 */
export const LAYOUT_RONGYUAN87_KEYCODES: number[][] =
  LAYOUT_HUAFENDA87_KEYCODES;

/** X75V2（贝盈 916）— Ins + 旋钮，右侧 Del / PgUp / PgDn；底行无 R-Alt，Fn/R-Ctrl 1.25U */
export const LAYOUT_X75V2_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 73, 0, 8418, 0, 0, 0,
    0,
  ], //17 补4个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 76, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 0, 0, 0, 0, 0, 0], //15 补6个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 78, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0, 0, 0, 0, 0], //13 补8个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 0, 61697, 228, 80, 81, 79, 0, 0, 0, 0,
    0,
  ], // 无 R-Alt；Fn / R-Ctrl 1.25U
];

/** X75GC（华丰达）— 配列基于 X75V2：PrtSc + Home + 右侧 Del/PgUp/PgDn */
export const LAYOUT_X75GC_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 74, 0, 0, 0, 0,
    0,
  ], // PrtSc 后紧跟 Home，无占位列
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 76, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 0, 0, 0, 0, 0, 0], //15 补6个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 78, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0, 0, 0, 0, 0], //13 补8个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 0, 80, 81, 79, 0, 0, 0, 0, 0,
    0,
  ], //15 无 RCtrl；RAlt/Fn 1.25U，Fn 与 ← 间 0.5U（同 BAT68）
];

/** M75（贝盈 916）— 75% 配列，Pause + 旋钮 + 右侧 Ins/Del/PgUp/PgDn 导航列 */
export const LAYOUT_M75_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 72, 0, 8418, 0, 0, 0,
    0,
  ], //17 补4个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0], //15 补6个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ], //16 补5个0
];

/** 星闪 X75 V3：75% 紧凑配列（82 键），各行右缘与 Insert 纵向对齐 */
export const LAYOUT_X75V3_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 73, 0, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0, 0, 0],
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ],
];

/** EXX Gamma75：与 x75v3 同构（无 App/Menu 键） */
export const LAYOUT_GAMMA75_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 73, 0, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0, 0, 0],
  /** Shift 行 LShift 后 col1 为矩阵占位列（与 ES68 一致，轴体/性能 API 列号对齐） */
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ],
];

/** DK75 Pro / ZE75：F 行 Esc 后分组间隔 + 末两格小屏/旋钮占位；底行无 RCtrl */
export const LAYOUT_DK75PRO_KEYCODES: number[][] = [
  [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 0, 0, 0, 0, 0, 0, 0, 0],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 0, 0, 0, 0, 0,
    0,
  ],
];

/** DK75 E：与 DK75 Pro 同构，F 行末为 PrtScr + Insert */
export const LAYOUT_DK75E_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 73, 0, 0, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 0, 0, 0, 0, 0,
    0,
  ],
];

/** DK75 标准款：F 行末 PrtScr 实键 + 旋钮占位（col14=0） */
export const LAYOUT_DK75_KEYCODES: number[][] = [
  [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 0, 0, 0, 0, 0, 0, 0],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 0, 0, 0, 0, 0,
    0,
  ],
];

export const LAYOUT_75PRO_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 62208, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0], //15 补6个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ], //16 补5个0
];

export const LAYOUT_M98_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 75, 78, 74, 8418, 0,
    0, 0,
  ], //18 补3个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86, 0,
    0, 0,
  ], //18 补3个0
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87, 0, 0,
    0,
  ], //18 补3个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0, 0, 0, 0, 0], //17 补4个0
  // Enter 与底行 0 同列：竖跨逻辑对 M98 单独放行 0，避免被盖住
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99, 0,
    0, 0,
  ],
];

export const LAYOUT_M98TFT_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 0, 0, 0, 8418, 0, 0,
    0,
  ], //18 补3个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86, 0,
    0, 0,
  ], //18 补3个0
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87, 0, 0,
    0,
  ], //18 补3个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0, 0, 0, 0, 0], //17 补4个0
  // Enter 与底行 0 同列：竖跨逻辑对 M98 单独放行 0，避免被盖住
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99, 0,
    0, 0,
  ],
];

export const LAYOUT_M98_8K_KEYCODES: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 0, 0, 0, 8418, 0, 0,
    0,
  ], //18 补3个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86, 0,
    0, 0,
  ], //18 补3个0
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87, 0, 0,
    0,
  ], //18 补3个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0, 0, 0, 0, 0], //17 补4个0
  // Enter 与底行 0 同列：竖跨逻辑对 M98 单独放行 0，避免被盖住
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88, 0, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99, 0,
    0, 0,
  ],
];

export const LAYOUT_75PROTFT_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 62208, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
    0,
  ], //15 补6个0
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0, 0], //15 补6个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0, 0, 0, 0, 0], //14 补7个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ], //16 补5个0
];
/** 84% TKL（SMART875）— 与 KEYCODE_MAP_HFD_LAYOUT["84"] 一致 */
export const LAYOUT_84_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 70, 72, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 75, 0, 0, 0, 0,
    0,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 78, 0, 0, 0, 0,
    0,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],

  // Row 4: Shift + ZXCV + Shift + 方向上 (21 cols)，与 KEYCODE_MAP_HFD_LAYOUT["84"] 一致
  [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0],

  // ✅ Row 5: 控制行 + 方向键 (21 cols)
  // ✅ 修正：Col 11 改为 61697（80% 键盘的 Fn 键）
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ],
];

/** DEEP80 MAX：虚拟键盘与 SMART875(84) 完全一致 */
export const LAYOUT_DEEP80MAX_KEYCODES: number[][] = LAYOUT_84_KEYCODES;

/** 84% TKL 键盘 KeyCode 矩阵（6行） */
export const LAYOUT_99_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 73, 74, 75, 78,
    0, 0,
  ], // 19 补2个0
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86, 0,
    0, 0,
  ], // 18 补3个0
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87, 0, 0,
    0,
  ], // 18 补3个0
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0, 0, 0, 0, 0], // 16 补5个0
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 89, 90, 91, 88,
    0,
  ], // 20 补1个0
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99, 0,
    0, 0,
  ], // 10 补11个0
];
/** 84% TKL 键盘 KeyCode 矩阵（6行） */
export const LAYOUT_98_KEYCODES: number[][] = [
  // Row 0: Esc + F1-F12 + 系统键 (18 keys)
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 8594, 74, 77, 72,
    0, 0, 0,
  ],

  // Row 1: 数字行 + Backspace + 导航键 (19 cols)
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 83, 84, 85, 86,
    0, 0,
  ],

  // Row 2: Tab + 导航键 (19 cols)
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 95, 96, 97, 87,
    0, 0,
  ],

  // Row 3: Caps + ASDF + Enter (17 cols)
  [
    57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 78, 92, 93, 94, 0, 0, 0,
    0,
  ],

  // Row 4: Shift + ZXCV + Shift + 方向上 (17 cols)
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 89, 90, 91, 88, 0, 0,
    0, 0,
  ],

  // ✅ Row 5: 控制行 + 方向键 (11 cols)
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 98, 99, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0,
  ],
];
/** 贝盈 X98 1K 默认矩阵（与 98% 分位、getKeyboardKeys pos 序一致） */
export const LAYOUT_X98_1K_KEYCODES: number[][] = LAYOUT_98_KEYCODES;

/**
 * 达克 V102（k100）：F 行 Ins 后三格 0 = 屏+Media 留白（不渲染 Media 键）。
 * 协议映射见 v102UiToProtocolMap.json。
 */
export const LAYOUT_V102_KEYCODES: number[][] = [
  [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 73, 0, 0, 0, 0, 0, 0],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 75, 83, 84, 85, 86,
    0, 0,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 78, 95, 96, 97, 87,
    0, 0,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 76, 92, 93, 94, 0, 0, 0, 0],
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 89, 90, 91, 88, 0, 0,
    0,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99, 0,
    0, 0,
  ],
];

/** M80 / M71 机甲键盘（与 KEYBOARD_LAYOUTS.m80 / m71 一致，6×19，星闪 keycode） */
export const LAYOUT_M80_KEYCODES: number[][] = [
  [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 76, 0, 0, 0, 0],
  [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 0, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0],
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0],
  [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0],
];

/** M71 默认与 M80 同构；硬件差异时单独改此矩阵 */
export const LAYOUT_M71_KEYCODES: number[][] = [
  [0],
  [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 0, 0, 0],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 75, 0, 0, 0],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 76, 78, 0, 0, 0, 0],
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 21252, 228, 80, 81, 79, 0,
    0, 0,
  ],
];

/** T87（贝盈 916 机甲，6×17，物理配列对齐 X87 / huafenda87；须与 KEYBOARD_LAYOUTS.t87 一致） */
export const LAYOUT_T87_KEYCODES: number[][] = [
  [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0],
  [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75],
  [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0],
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 82, 0, 0, 0],
  [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79],
];

export const LAYOUT_HUAFENDA98_KEYCODES: number[][] = [
  // Row 0: Esc + F1-F12 + 系统键 (18 keys)
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 8594, 74, 77, 72,
    0, 0, 0,
  ],

  // Row 1: 数字行 + Backspace + 导航键 (19 cols)
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 83, 84, 85, 86,
    0, 0,
  ],

  // Row 2: Tab + 导航键 (19 cols)
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 95, 96, 97, 87,
    0, 0,
  ],

  // Row 3: Caps + ASDF + Enter (17 cols)
  [
    57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 78, 92, 93, 94, 0, 0, 0,
    0,
  ],

  // Row 4: Shift + ZXCV + Shift + 方向上 (17 cols)
  [
    225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 89, 90, 91, 88, 0, 0,
    0, 0,
  ],

  // ✅ Row 5: 控制行 + 方向键 (11 cols)
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 98, 99, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0,
  ],
];

/**
 * 标准 ANSI 100%（104）键码矩阵：完整逐格写出，不与 87/其它配列拼接生成。
 * 行序：F 区 → 数字行 → Tab 行 → Caps 行 → Shift 行 → 底行（含编辑/方向/小键盘侧）。
 */
export const LAYOUT_104_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 83, 84,
    85, 86,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 95, 96,
    97, 87,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 92, 93, 94, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 89, 90, 91,
    88,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 98,
    99, 0, 0,
  ],
];

/** DK108：108 键全尺寸 ANSI（6×21），独立维护；已与实机 SDK getKeyData 校准通过 */
export const LAYOUT_DK108_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 8374,
    8397, 8373, 62208,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 83, 84,
    85, 86,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 95, 96,
    97, 87,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 92, 93, 94, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 89, 90, 91,
    88,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 98,
    99, 0, 0,
  ],
];

/**
 * 改键页「基础按键」源盘：与上表同维同结构，独立维护；
 * 底行原 Fn1 格统一为右 Win / R-GUI（231），勿改各机型真机布局里的物理 Fn。
 */
export const LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES: number[][] = [
  [
    41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 83, 84,
    85, 86,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 95, 96,
    97, 87,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 92, 93, 94, 0],
  [
    225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 89, 90, 91,
    88,
  ],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 231, 101, 228, 80, 81, 79, 98, 99,
    0, 0,
  ],
];

/**
 * 根据键盘类型获取 KeyCode 矩阵
 */
export const getKeyCodeMatrix = (keyboardType: AllKeyboardType): number[][] => {
  // 判断方案商，用于显示隐藏改键类型及键码
  const equipmentData: any = localStorage.getItem("equipmentData");
  let solutionRovider: any = "xingshan";
  let equipmentKeyboardType = "";
  let equipmentVendorId: number | undefined;
  let equipmentProductId: number | undefined;
  if (equipmentData) {
    const parsed = JSON.parse(equipmentData);
    solutionRovider = parsed.solutionRovider;
    equipmentKeyboardType = parsed.keyboardType ?? "";
    equipmentVendorId = parsed.vendorId;
    equipmentProductId = parsed.productId;
  }
  // 检查是否属于额外按键类型的键盘
  const isExtraKeyType = [
    "media",
    "tool",
    "mouse",
    "macro",
    "light",
    "control",
    "special",
  ].includes(keyboardType);

  if (isExtraKeyType) {
    if (solutionRovider === "beiying") {
      return EXTRA_KEYS_MATRIX_BEIYING[
        keyboardType as keyof typeof EXTRA_KEYS_MATRIX_BEIYING
      ];
    } else if (solutionRovider === "ZHWX") {
      return EXTRA_KEYS_MATRIX_ZHWX[
        keyboardType as keyof typeof EXTRA_KEYS_MATRIX_ZHWX
      ];
    } else if (solutionRovider === "rongyuan") {
      return EXTRA_KEYS_MATRIX_RONGYUAN[
        keyboardType as keyof typeof EXTRA_KEYS_MATRIX_RONGYUAN
      ];
    } else if (solutionRovider === "huafenda") {
      return EXTRA_KEYS_MATRIX_HUAFENDA[
        keyboardType as keyof typeof EXTRA_KEYS_MATRIX_HUAFENDA
      ];
    } else {
      if (keyboardType === "macro") {
        return resolveMacroKeyCodeMatrix({
          solutionRovider,
          keyboardType: equipmentKeyboardType,
          vendorId: equipmentVendorId,
          productId: equipmentProductId,
        });
      }
      return EXTRA_KEYS_MATRIX[keyboardType as keyof typeof EXTRA_KEYS_MATRIX];
    }
  }
  switch (keyboardType) {
    case "104":
      return LAYOUT_104_KEYCODES;
    case "keymap104":
      return LAYOUT_CHANGEKEY_PALETTE_104_KEYCODES;
    case "84":
      return LAYOUT_84_KEYCODES;
    case "deep80max":
      return LAYOUT_DEEP80MAX_KEYCODES;
    case "87":
      return LAYOUT_87_KEYCODES;
    case "es87":
      return LAYOUT_ES87_KEYCODES;
    case "huafenda87":
      return LAYOUT_HUAFENDA87_KEYCODES;
    case "rongyuan87":
      return LAYOUT_RONGYUAN87_KEYCODES;
    case "63":
      return LAYOUT_63_KEYCODES;
    case "68":
      return LAYOUT_68_KEYCODES;
    case "huafenda68":
      return LAYOUT_HUAFENDA68_KEYCODES;
    case "tank68he":
      return LAYOUT_TANK68HE_KEYCODES;
    case "68EVO":
      return LAYOUT_68EVO_KEYCODES;
    case "99":
      return LAYOUT_99_KEYCODES;
    case "98":
      return LAYOUT_98_KEYCODES;
    case "v102":
      return LAYOUT_V102_KEYCODES;
    case "X98_1K":
      return LAYOUT_X98_1K_KEYCODES;
    case "m80":
      return LAYOUT_M80_KEYCODES;
    case "m71":
      return LAYOUT_M71_KEYCODES;
    case "t87":
      return LAYOUT_T87_KEYCODES;
    case "x75v2":
      return LAYOUT_X75V2_KEYCODES;
    case "m75":
      return LAYOUT_M75_KEYCODES;
    case "x75gc":
      return LAYOUT_X75GC_KEYCODES;
    case "huafenda98":
      return LAYOUT_HUAFENDA98_KEYCODES;
    case "75Pro":
      return LAYOUT_75PRO_KEYCODES;
    case "x75v3":
      return LAYOUT_X75V3_KEYCODES;
    case "gamma75":
      return LAYOUT_GAMMA75_KEYCODES;
    case "dk75":
      return LAYOUT_DK75_KEYCODES;
    case "dk75Pro":
      return LAYOUT_DK75PRO_KEYCODES;
    case "dk75E":
      return LAYOUT_DK75E_KEYCODES;
    case "dk87":
      return LAYOUT_DK87_KEYCODES;
    case "dk75Single":
      return LAYOUT_DK75_SINGLE_KEYCODES;
    case "dk63Machine":
      return LAYOUT_DK63_MACHINE_KEYCODES;
    case "M98":
      return LAYOUT_M98_KEYCODES;
    case "M98TFT":
      return LAYOUT_M98TFT_KEYCODES;
    case "DK108":
      return LAYOUT_DK108_KEYCODES;
    case "M98 8K":
      return LAYOUT_M98_8K_KEYCODES;
    case "75ProTFT":
      return LAYOUT_75PROTFT_KEYCODES;
    default:
      return EXTRA_KEYS_MATRIX[keyboardType as keyof typeof EXTRA_KEYS_MATRIX];
  }
};
